"use client";

import {v4 as uuidv4} from "uuid";
import {createContext, useContext, useEffect, useState} from "react";
import {FCWithChildren} from "@/app/types/reactTypes";
import {LEGACY_SITE_URL} from "@/app/util/getLegacyCampusURL";
import * as _ from "lodash-es";
import {useCampusContext} from "./CampusCookieContext";

type ClientSourceTrackingContextType = {
	clientDeviceId?: string;
};

const ClientSourceTrackingContext =
	createContext<ClientSourceTrackingContextType>({});

export const ClientSourceTrackingContextProvider =
	ClientSourceTrackingContext.Provider;

export const useClientSourceTrackingContext = () => {
	return useContext(ClientSourceTrackingContext);
};

export const useClientDeviceId = () => {
	const {clientDeviceId} = useClientSourceTrackingContext();

	return clientDeviceId;
};

/**
 * The UTM parameters that we care about
 */
type UtmParams = {
	utmSource?: string;
	utmMedium?: string;
	utmCampaign?: string;
	utmContent?: string;
	utmTerm?: string;
};

/**
 * Parses and returns the relevant UTM parameters from search params
 */
function getUTMParams() {
	const params = new URLSearchParams(window.location.search);
	const parsedParams: UtmParams = {
		utmSource: params.get("utm_source") ?? undefined,
		utmMedium: params.get("utm_medium") ?? undefined,
		utmCampaign: params.get("utm_campaign") ?? undefined,
		utmContent: params.get("utm_content") ?? undefined,
		utmTerm: params.get("utm_term") ?? undefined,
	};

	return parsedParams;
}

/**
 * Records a UTM source for a browser.
 * 	This tells us that this browser followed a specific ad to get to our website
 * @param utmParams All relevant search params from the browser
 * @param chosenClientDeviceId The client's device id, found from `window.localStorage.getItem("cdid")`
 * @param chosenClientDeviceId The client's campus, found from the campus cookie
 */
function recordUTMParams(
	utmParams: UtmParams,
	chosenClientDeviceId: string,
	campusId: string
) {
	const ajaxBody: Record<string, string> = {
		a: "record_source_with_campus",
		client_device_id: chosenClientDeviceId,
		campus_id: campusId,
	};

	Object.entries(utmParams).forEach(([key, value]) => {
		// Filter out undefined utmParams
		if (!value) {
			return;
		}

		// The PHP side expects the keys to be snake_case
		const snakeCaseKey = _.snakeCase(key);
		ajaxBody[snakeCaseKey] = value;
	});

	// Convert ajax body to a URL query params for the x-www-form-urlencoded header
	const ajaxBodyUrlEncoded = new URLSearchParams(ajaxBody);

	return fetch(`${LEGACY_SITE_URL}s/sourceRecording/ajax.php`, {
		method: "POST",
		headers: {
			"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
		},
		body: ajaxBodyUrlEncoded,
	});
}

/**
 * Gets the client device id from localstorage. If it's unset, sets it to a new uuidv4, then returns it
 */
function getClientDeviceId() {
	const currentClientDeviceId = window.localStorage.getItem("cdid");

	if (currentClientDeviceId) {
		return currentClientDeviceId;
	}

	// If no client device id is set, set a new one in localstorage
	const newClientDeviceId = uuidv4();
	window.localStorage.setItem("cdid", newClientDeviceId);
	return newClientDeviceId;
}

/**
 * A component that on page load sets up the ClientSourceTrackingContext
 * 	If a UTM source is set, it also records that source
 */
export const ClientSourceTrackingWrapperClient: FCWithChildren = ({
	children,
}) => {
	const [clientDeviceId, setClientDeviceId] = useState<string | undefined>();
	const {campus, isInitialized: isCampusInitialized} = useCampusContext();
	const campusId = campus?.campusId;

	useEffect(() => {
		const browserClientDeviceId = getClientDeviceId();
		setClientDeviceId(browserClientDeviceId);

		// Check if a utm source is set.
		// If so, records it and other utm params along with the client device id
		const utmParams = getUTMParams();
		if (utmParams.utmSource && isCampusInitialized) {
			recordUTMParams(utmParams, browserClientDeviceId, campusId ?? "");
		}
	}, [campusId, isCampusInitialized]);

	return (
		<ClientSourceTrackingContextProvider value={{clientDeviceId}}>
			{children}
		</ClientSourceTrackingContextProvider>
	);
};
